import { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { downloadPdf } from "@/lib/files";
import { Button } from "@/components/ui/button";
import { KpiCard } from "@/components/KpiCard";
import { PageHeader } from "@/components/PageHeader";
import { fmtDA } from "@/lib/format";
import {
  Receipt, CalendarClock, CalendarRange, Wallet, Landmark, Boxes,
  FileWarning, AlertTriangle, TrendingUp, ShoppingBag, HandCoins, Package, BellRing, Trophy, Building2, Factory, ArrowRight, FileDown,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell,
} from "recharts";
import { Loader2 } from "lucide-react";
import { CaAnnuelCard } from "@/components/dashboard/CaAnnuelCard";
import { VentesMensuellesCard } from "@/components/dashboard/VentesMensuellesCard";
import { CaMensuelCard } from "@/components/dashboard/CaMensuelCard";

const PIE_COLORS = ["#2563eb", "#16a34a", "#f59e0b", "#7c3aed", "#0891b2", "#db2777", "#65a30d", "#dc2626"];

export default function Dashboard() {
  const { societeParam, societeId, exercice } = useApp();
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState(null);

  const dashParams = { ...societeParam, ...(exercice === "all" ? { all_years: true } : { annee: exercice }) };

  useEffect(() => {
    setData(null);
    api.get("/dashboard", { params: dashParams }).then((r) => setData(r.data)).catch(() => setData(false));
    api.get("/notifications", { params: societeParam }).then((r) => setAlerts(r.data)).catch(() => setAlerts(false));
  }, [societeId, exercice]); // eslint-disable-line

  const markAllRead = () => {
    api.post("/notifications/read-all", null, { params: societeParam }).then(() => {
      api.get("/notifications", { params: societeParam }).then((r) => setAlerts(r.data));
    });
  };

  if (data === null) {
    return <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }
  if (data === false) return <p className="text-destructive">Erreur de chargement.</p>;

  return (
    <div>
      <PageHeader
        title="Tableau de bord"
        subtitle="Vue d'ensemble de votre activité financière"
        testid="dashboard-page"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 kpi-grid">
        <KpiCard testid="kpi-dep-jour" label="Dépenses aujourd'hui" value={data.depenses_jour} icon={Receipt} tone="primary" />
        <KpiCard testid="kpi-dep-mois" label="Dépenses du mois" value={data.depenses_mois} icon={CalendarClock} />
        <KpiCard testid="kpi-dep-annee" label="Dépenses annuelles" value={data.depenses_annee} icon={CalendarRange} />
        <KpiCard testid="kpi-fact-payer" label="Factures à payer" value={data.factures_a_payer} icon={FileWarning} tone="warning" sub={`${data.nb_factures_a_payer} facture(s)`} />
        <KpiCard testid="kpi-ventes-mois" label="Ventes du mois" value={data.ventes_mois} icon={ShoppingBag} tone="success" />
        <KpiCard testid="kpi-ventes-annee" label="Ventes annuelles (TTC)" value={data.ventes_annee} icon={TrendingUp} tone="success" />
        <KpiCard testid="kpi-ca-ht-annee" label="Chiffre d'affaires HT" value={data.ca_ht_annee} icon={TrendingUp} tone="success" sub={`Mois : ${fmtDA(data.ca_ht_mois)}`} />
        <KpiCard testid="kpi-creances" label="Créances clients" value={data.creances_clients} icon={HandCoins} tone="warning" />
        <KpiCard testid="kpi-valeur-stock" label="Valeur du stock" value={data.valeur_stock} icon={Package} tone="primary" />
        <KpiCard testid="kpi-caisse" label="Solde caisse" value={data.solde_caisse} icon={Wallet} tone="success" />
        <KpiCard testid="kpi-banque" label="Solde banque" value={data.solde_banque} icon={Landmark} tone="success" />
        <KpiCard testid="kpi-retard" label="Factures en retard" value={data.factures_en_retard} icon={AlertTriangle} tone="danger" />
        <KpiCard testid="kpi-tresorerie" label="Trésorerie totale" value={(data.solde_caisse || 0) + (data.solde_banque || 0)} icon={Boxes} tone="primary" />
      </div>

      {data.ca_ht_par_societe && data.ca_ht_par_societe.length > 0 && (
        <div className="rounded-xl border bg-card p-5 mt-6" data-testid="dashboard-ca-ht-societe">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="h-4 w-4 text-primary" />
            <h3 className="font-heading font-bold">Chiffre d'affaires HT par société</h3>
          </div>
          <div className="divide-y">
            {data.ca_ht_par_societe.map((s) => (
              <div key={s.societe_id} className="flex items-center justify-between py-2.5" data-testid={`ca-ht-societe-${s.societe_id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{s.nom}</p>
                  <p className="text-[11px] text-muted-foreground">{s.nb} facture(s)</p>
                </div>
                <span className="tabular text-sm font-semibold text-success shrink-0 ml-2">{fmtDA(s.ca_ht)}</span>
              </div>
            ))}
            <div className="flex items-center justify-between py-2.5 border-t-2 border-primary/30">
              <span className="text-sm font-bold">Total HT</span>
              <span className="tabular text-sm font-bold text-success" data-testid="ca-ht-total">{fmtDA(data.ca_ht_par_societe.reduce((a, s) => a + s.ca_ht, 0))}</span>
            </div>
          </div>
        </div>
      )}

      <CaAnnuelCard societeId={societeId} />
      <CaMensuelCard societeId={societeId} exercice={exercice} />
      <VentesMensuellesCard societeId={societeId} exercice={exercice} />

      <div className="rounded-xl border bg-card p-5 mt-6" data-testid="dashboard-ventes-produit">
        <div className="flex items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <ShoppingBag className="h-4 w-4 text-primary" />
            <h3 className="font-heading font-bold">Quantités vendues par produit</h3>
          </div>
          {data.ventes_par_produit && data.ventes_par_produit.length > 0 && (
            <Button variant="outline" size="sm" className="gap-2" data-testid="ventes-produit-pdf-btn"
              onClick={() => {
                const p = new URLSearchParams();
                if (societeParam.societe_id) p.set("societe_id", societeParam.societe_id);
                if (exercice === "all") p.set("all_years", "true"); else p.set("annee", exercice);
                downloadPdf(`/pdf/ventes-produit?${p.toString()}`, "quantites-vendues-par-produit");
              }}>
              <FileDown className="h-4 w-4" /> PDF
            </Button>
          )}
        </div>
        {data.ventes_par_produit && data.ventes_par_produit.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b">
                  <th className="py-2 font-medium">Produit</th>
                  <th className="py-2 font-medium text-right">Quantité vendue</th>
                  <th className="py-2 font-medium text-right">CA HT</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {data.ventes_par_produit.map((p, i) => (
                  <tr key={i} data-testid={`vente-produit-${i}`}>
                    <td className="py-2.5 pr-2">{p.produit}</td>
                    <td className="py-2.5 tabular text-right font-semibold">{new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(p.quantite)} {p.unite}</td>
                    <td className="py-2.5 tabular text-right text-success">{fmtDA(p.montant_ht)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-6 text-center" data-testid="ventes-produit-empty">Aucune vente enregistrée pour cette période.</p>
        )}
      </div>

      <div className="rounded-xl border bg-card p-5 mt-6" data-testid="dashboard-productions">
        <div className="flex items-center gap-2 mb-4">
          <Factory className="h-4 w-4 text-primary" />
          <h3 className="font-heading font-bold">Productions récentes — matières premières consommées & produit fini</h3>
        </div>
        {data.productions_recentes && data.productions_recentes.length > 0 ? (
          <div className="space-y-3">
            {data.productions_recentes.map((p) => (
              <div key={p.id} className="rounded-lg border bg-muted/20 p-4" data-testid={`production-${p.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="inline-flex items-center gap-1.5 rounded-md bg-success/10 text-success px-2.5 py-1 text-sm font-semibold">
                      <Package className="h-3.5 w-3.5" /> {p.produit_name}
                    </span>
                    <span className="tabular text-sm font-bold">{new Intl.NumberFormat("fr-FR").format(p.quantite_produite)} {p.produit_unite}</span>
                  </div>
                  <span className="text-[11px] text-muted-foreground shrink-0">{p.numero} · {p.date} · {p.societe_nom}</span>
                </div>
                {p.consommations && p.consommations.length > 0 ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    {p.consommations.map((c, i) => (
                      <span key={i} className="inline-flex items-center gap-1 rounded-md bg-card border px-2 py-1 text-xs" data-testid={`production-${p.id}-mp-${i}`}>
                        <span className="text-muted-foreground">{c.produit_name}</span>
                        <span className="tabular font-semibold">{new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 3 }).format(c.quantite)} {c.unite}</span>
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">Aucune matière première consommée enregistrée.</p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-6 text-center" data-testid="productions-empty">Aucune production enregistrée. Saisissez une production (module Production) pour voir ici le produit fini et les quantités de matières premières consommées.</p>
        )}
      </div>

      {data.produit_phare && (
        <div className="rounded-xl border bg-card p-5 mt-6" data-testid="dashboard-produit-phare">
          <div className="flex items-center gap-2 mb-4">
            <Package className="h-4 w-4 text-primary" />
            <h3 className="font-heading font-bold">Produit — désignation</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-1 rounded-lg bg-primary/5 border border-primary/20 p-4 flex flex-col justify-center">
              <p className="text-xs text-muted-foreground mb-1">Désignation</p>
              <p className="font-heading font-bold text-base leading-snug" data-testid="produit-phare-designation">{data.produit_phare.designation}</p>
            </div>
            <div className="rounded-lg bg-muted/40 p-4 flex flex-col justify-center">
              <p className="text-xs text-muted-foreground mb-1">Cartons vendus</p>
              <p className="font-heading font-bold text-2xl tabular" data-testid="produit-phare-quantite">
                {new Intl.NumberFormat("fr-FR").format(data.produit_phare.quantite_vendue)}
              </p>
              <p className="text-[11px] text-muted-foreground mt-0.5">{data.produit_phare.nb_ventes} vente(s)</p>
            </div>
            <div className="rounded-lg bg-success/5 p-4 flex flex-col justify-center">
              <p className="text-xs text-muted-foreground mb-1">Chiffre d'affaires HT</p>
              <p className="font-heading font-bold text-2xl tabular text-success" data-testid="produit-phare-ca">{fmtDA(data.produit_phare.ca)}</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <div className="lg:col-span-2 rounded-xl border bg-card p-5">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-4 w-4 text-primary" />
            <h3 className="font-heading font-bold">Évolution des dépenses (6 mois)</h3>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.chart_mensuel} margin={{ left: 4, right: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="mois" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={70}
                tickFormatter={(v) => new Intl.NumberFormat("fr-FR", { notation: "compact" }).format(v)} />
              <Tooltip formatter={(v) => fmtDA(v)} contentStyle={{ borderRadius: 8, border: "1px solid hsl(var(--border))", fontSize: 13 }} />
              <Bar dataKey="depenses" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} maxBarSize={48} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl border bg-card p-5">
          <h3 className="font-heading font-bold mb-4">Dépenses par catégorie</h3>
          {data.depenses_par_categorie?.length ? (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={data.depenses_par_categorie.slice(0, 8)} dataKey="total" nameKey="categorie" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2}>
                    {data.depenses_par_categorie.slice(0, 8).map((e, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmtDA(v)} contentStyle={{ borderRadius: 8, fontSize: 13 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1.5 mt-3">
                {data.depenses_par_categorie.slice(0, 5).map((c, i) => (
                  <div key={c.categorie} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 truncate">
                      <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <span className="truncate">{c.categorie}</span>
                    </span>
                    <span className="tabular text-muted-foreground shrink-0 ml-2">{fmtDA(c.total)}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground py-8 text-center">Aucune dépense enregistrée.</p>
          )}
        </div>
      </div>

      {data.top_clients && data.top_clients.length > 0 && (
        <div className="rounded-xl border bg-card p-5 mt-6" data-testid="dashboard-top-clients">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="h-4 w-4 text-primary" />
            <h3 className="font-heading font-bold">Top clients par chiffre d'affaires</h3>
          </div>
          <div className="space-y-2">
            {data.top_clients.map((c, i) => {
              const max = data.top_clients[0].ca || 1;
              const pct = Math.max(4, Math.round((c.ca / max) * 100));
              return (
                <div key={c.client_id} className="flex items-center gap-3" data-testid={`top-client-${i}`}>
                  <span className="h-6 w-6 shrink-0 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-sm font-medium truncate">{c.client_name}</span>
                      <span className="tabular text-sm font-semibold shrink-0">{fmtDA(c.ca)}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                      <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="flex items-center justify-between mt-0.5">
                      <span className="text-[11px] text-muted-foreground">{c.nb} facture(s)</span>
                      {c.reste > 0 && <span className="text-[11px] text-warning">Reste {fmtDA(c.reste)}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {alerts && alerts.items?.length > 0 && (
        <div className="rounded-xl border bg-card p-5 mt-6" data-testid="dashboard-alertes-auto">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-bold flex items-center gap-2">
              <BellRing className="h-4 w-4 text-warning" /> Alertes automatiques
              {alerts.unread > 0 && (
                <span className="ml-1 inline-flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[11px] font-semibold h-5 min-w-[20px] px-1.5" data-testid="alertes-unread-badge">
                  {alerts.unread}
                </span>
              )}
            </h3>
            {alerts.unread > 0 && (
              <button
                onClick={markAllRead}
                data-testid="alertes-mark-all-read"
                className="text-xs font-medium text-primary hover:underline"
              >
                Tout marquer comme lu
              </button>
            )}
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Impayés et ruptures de stock — générés automatiquement chaque matin.
          </p>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {alerts.items.map((n) => {
              const Icon = n.category === "stock" ? Package : FileWarning;
              const tone = n.type === "danger" ? "text-destructive" : "text-warning";
              const bg = n.type === "danger" ? "bg-destructive/5" : "bg-warning/5";
              return (
                <div
                  key={n.id}
                  data-testid={`alerte-item-${n.id}`}
                  className={`flex items-start gap-2.5 text-sm rounded-lg px-3 py-2 ${bg} ${n.read ? "opacity-60" : ""}`}
                >
                  <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${tone}`} />
                  <div className="flex-1 min-w-0">
                    <p className="leading-snug">{n.message}</p>
                    {n.societe_nom && <p className="text-[11px] text-muted-foreground mt-0.5">{n.societe_nom}</p>}
                  </div>
                  {!n.read && <span className="h-2 w-2 rounded-full bg-primary shrink-0 mt-1.5" />}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
